import { Empty, Toast } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import { AnimationDecorators, useContainerBlockContext, useCustomViewBlockContext, ViewBlockProvider } from '@lighthouse/block'
import { KanbanBoardBlock } from '@lighthouse/block/src/blocks'
import type {
    ActorEvent,
    FieldADTValue,
    FieldValue,
    FilterCommonCondition,
    RecordLikeProtocol,
    RichTextContentProtocol,
    ViewControlHorizontalScrollToAction,
    ViewControlScrollToRecordAction
} from '@lighthouse/core'
import type { ViewAppendParams } from '@lighthouse/shared'
import {
    CenteredWrapper,
    findRecordInPool,
    getWithScopeIdAndRecordIdAndColumnId,
    KANBAN_PAGE_SIZE,
    pageStackPubSub,
    scroll2EndLeftOrRight,
    scroll2ViewItem,
    scrollToRecord,
    useAtomAction,
    useAtomAsyncAction,
    useHandleAbortPrevious,
    useLanguageContext,
    useRegisterViewControlListeners,
    ViewBlockTool
} from '@lighthouse/shared'
import { useBreakpoint } from '@lighthouse/tools'
import { useStore } from 'jotai'
import { useAtomCallback } from 'jotai/utils'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'

import { fetchViewRecordsAtom } from '@/atoms/blockRecordsDict/action'
import { createRecordAtom, updateCellAtom } from '@/atoms/dataSource/action'
import { recordPoolAtom } from '@/atoms/dataSource/state'
import { useCurrentPageContext, useCurrentStackIdContext } from '@/contexts/PageContext'
import { useActionTrigger } from '@/hooks/useActionTrigger'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import * as srv from '@/services'

import { useConfigure } from '../hooks/useConfigure'
import { useFetchViewDataDeps } from '../hooks/useFetchViewDataDeps'
import { useFilter } from '../hooks/useFilter'
import { useGroup } from '../hooks/useGroup'
import { useKanbanColumnsSort } from '../hooks/useKanbanColumnsSort'
import { useLinkFilter } from '../hooks/useLinkFilter'
import { useQuickFilter } from '../hooks/useQuickFilter'
import { useRenderRichText } from '../hooks/useRenderRichText'
import { useSort } from '../hooks/useSort'
import { useSubscribeView } from '../hooks/useSubscribeView'
import { useTableProps } from '../hooks/useTableProps'
import { useViewIndependent } from '../hooks/useViewIndependent'
import type { FetchViewDataPayload, ViewControllerProps } from '../types'

const EmptyImage = styled.img`
    width: 140px;
    height: 144px;
`

const SCxViewContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 1px;
`

const KanbanController: React.FC<ViewControllerProps> = ({ blockData, firstRef, onReady, children, ...rest }) => {
    const { id, config, title } = blockData
    const {
        pointer,
        viewType,
        linkFilterController,
        quickFilter,
        groupConfigure,
        canGroup,
        canSort,
        canSearch,
        triggerWorkflow,
        importMode,
        repeat,
        compareFields,
        canPaginate,
        breakPoint: baseBreakpoint,
        toolbar,
        recordOperator,
        filter,
        sorts
    } = config

    const envId = useCurrentEnvId()

    const stackId = useCurrentStackIdContext()
    const { pageId } = useCurrentPageContext()
    const { scope } = useContainerBlockContext()
    const { record } = useCustomViewBlockContext()
    const uniqueId = useMemo(() => getWithScopeIdAndRecordIdAndColumnId(id, scope, record?.id), [id, record?.id, scope])

    const { handleActionListTrigger } = useActionTrigger(id)
    const appId = useCurrentAppId()

    const dataSourceList = useDataSourceList(appId, envId)
    const dataSource = useDataSource(appId, envId, pointer)
    const { run: updateCell } = useAtomAction(updateCellAtom)

    // 快捷筛选器hook
    const { quickFiltersRule, handleQuickFilter } = useQuickFilter({
        appId,
        envId,
        blockId: uniqueId,
        dataSource,
        quickFilter
    })

    // 筛选器hook
    const { resolvedFilter } = useFilter({
        filter,
        dataSourceList
    })

    // 联动筛选器hook
    const { resolvedLinkFilter } = useLinkFilter({
        appId,
        envId,
        pageId,
        linkFilterController
    })

    // 快捷筛选器hook
    const { tablePropsCache, handleDisplayChange } = useTableProps({
        appId,
        envId,
        blockId: uniqueId
    })

    // 排序hook
    const { sortCache, mergeSort, handleSorterChange } = useSort({
        appId,
        envId,
        blockId: uniqueId,
        canSort,
        sorts
    })

    // 分组hook
    const { initGroup, tabValue, tabList, handleTabChange, getFilterByTab, handleGroupChange, handleGetGroupRecordCount } = useGroup({
        appId,
        envId,
        blockId: uniqueId,
        dataSource,
        canGroup,
        groupConfigure
    })

    // // 看板hook
    const { kanbanColumnsSortCache, setKanbanColumnsSortCache } = useKanbanColumnsSort({
        appId,
        envId,
        blockId: uniqueId
    })

    // 视图独立数据hook
    const {
        searchVal,
        currentPage = 1,
        rowTotal,
        recordPoints,
        handleSearch
    } = useViewIndependent({
        blockId: uniqueId,
        pointer,
        canSearch
    })

    const viewIndependentDataOmitRecord = useMemo(
        () => ({
            dsId: pointer,
            currentPage,
            rowTotal,
            search: searchVal
        }),
        [currentPage, pointer, rowTotal, searchVal]
    )

    const { convertTextByLanguage } = useLanguageContext()

    const allRichtext = useMemo(() => {
        const toolbarRich = toolbar?.map(item => item.name) || []
        const recordOperatorRich = recordOperator?.map(item => item.name) || []

        return [...toolbarRich, ...recordOperatorRich, baseBreakpoint.emptyView?.title].filter(Boolean) as RichTextContentProtocol[]
    }, [toolbar, recordOperator, baseBreakpoint.emptyView?.title])
    const handleRenderRichText = useRenderRichText(allRichtext)

    const { loading, value: fetchViewRecordsValue, run: fetchViewRecords } = useAtomAsyncAction(fetchViewRecordsAtom)

    // 首批数据加载完成且渲染完成后调用 onReady
    const hasCalledReady = useRef(false)
    useEffect(() => {
        if (!loading && fetchViewRecordsValue && !hasCalledReady.current) {
            // 双重 requestAnimationFrame 确保 DOM 渲染完成
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    hasCalledReady.current = true
                    onReady?.()
                })
            })
        }
    }, [loading, onReady, fetchViewRecordsValue])

    const { mutation } = useHandleAbortPrevious(fetchViewRecords)
    const { ref, width: blockWidth, breakPoint } = useBreakpoint([!!fetchViewRecordsValue])

    const pageSize = KANBAN_PAGE_SIZE

    // 使用新的依赖管理方案，优化重新渲染（仅包含对象/数组类型字段）
    const stableDeps = useFetchViewDataDeps({
        quickFiltersRule,
        resolvedFilter,
        resolvedLinkFilter,
        sorts: mergeSort
    })

    const fetchViewData = useCallback(
        async (params: FetchViewDataPayload) => {
            const { pageNum } = params
            const isHasTab = !initGroup.groupByFieldId || (initGroup.groupByFieldId && tabValue)
            if (pointer && isHasTab) {
                const quickFiltersWidthTab: FilterCommonCondition[] = [...(stableDeps.quickFiltersRule || []), ...getFilterByTab(tabValue)]
                await mutation({
                    stackId,
                    pageId,
                    viewId: id,
                    uniqueId,
                    scope,
                    isSyncComponent: !!scope,
                    pagination: { currentPage: pageNum, pageSize },
                    params: {
                        quickFilters: quickFiltersWidthTab,
                        sorts: stableDeps.sorts,
                        linkFilter: stableDeps.resolvedLinkFilter,
                        filter: stableDeps.resolvedFilter
                    },
                    search: searchVal,
                    disableFetchCount: !canPaginate
                })
            }
        },
        [
            initGroup.groupByFieldId,
            tabValue,
            pointer,
            stableDeps.quickFiltersRule,
            stableDeps.sorts,
            stableDeps.resolvedLinkFilter,
            stableDeps.resolvedFilter,
            getFilterByTab,
            mutation,
            stackId,
            pageId,
            id,
            uniqueId,
            scope,
            pageSize,
            searchVal,
            canPaginate
        ]
    )

    const getGroupRecordCount = useCallback(
        () =>
            handleGetGroupRecordCount({
                quickFilters: stableDeps.quickFiltersRule,
                filter: stableDeps.resolvedFilter,
                linkFilter: stableDeps.resolvedLinkFilter,
                search: searchVal
            }),
        [handleGetGroupRecordCount, stableDeps.quickFiltersRule, stableDeps.resolvedFilter, stableDeps.resolvedLinkFilter, searchVal]
    )

    const handleAction = useCallback(
        (id: string, actorEvent: ActorEvent) => handleActionListTrigger(id, actorEvent, { viewRecord: record }),
        [handleActionListTrigger, record]
    )
    const handleRecordOperatorAction = useCallback(
        (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) =>
            handleActionListTrigger(id, actorEvent, { viewRecord: record }),
        [handleActionListTrigger]
    )

    const handleRecordClickEvent = useCallback(
        (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) =>
            handleActionListTrigger(id, actorEvent, { viewRecord: record }),
        [handleActionListTrigger]
    )

    useConfigure({
        blockData,
        fetchViewData,
        getGroupRecordCount
    })

    // 接受其他命令执行加载数据操作
    useSubscribeView({ pointer, fetchViewData, pageNum: currentPage })

    const quickFilterData = useMemo(() => {
        return {
            mode: quickFilter?.mode || 'normal',
            rules: quickFiltersRule
        }
    }, [quickFilter?.mode, quickFiltersRule])

    const handleChangePageNum = useCallback(
        (pageNum: number) => {
            fetchViewData({
                pageNum
            })
        },
        [fetchViewData]
    )

    const handleViewRefresh = useCallback(() => {
        fetchViewData({ pageNum: 1 })
    }, [fetchViewData])

    const handleValueChange = useCallback(
        (recordId: string, fieldValue: FieldADTValue) => {
            return updateCell({ envId, dsId: pointer, recordId, value: fieldValue, fieldId: fieldValue.id })
        },
        [envId, pointer, updateCell]
    )

    const handleClickAdd = useAtomCallback(
        useCallback(
            async (get, set, content?: Record<string, FieldValue>) => {
                const record = await set(createRecordAtom, { envId, dsId: pointer, content })
                if (record) {
                    pageStackPubSub.emit(`${pointer}-ADD`, record)
                }

                return record
            },
            [envId, pointer]
        )
    )

    const store = useStore()

    // 扔到上层
    useRegisterViewControlListeners(id, scope, 'view', {
        refresh: handleViewRefresh,
        horizontalScrollTo: (payload: ViewControlHorizontalScrollToAction['params'] | undefined) => {
            const to = payload?.horizontalScrollTo
            const horizontalScrollDirection = payload?.horizontalScrollDirection
            const viewContainer = ref.current
            const items = viewContainer?.querySelectorAll('[data-view-item]')
            const viewItemsWrapper = items?.[0]?.parentElement
            if (!to || !items || !viewItemsWrapper) {
                return
            }
            const viewContentContainer = viewItemsWrapper?.parentElement
            const currentScrollLeft = viewItemsWrapper?.parentElement?.scrollLeft ?? 0
            const itemOffsetWidth = items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().left
            const currentIndex = Math.round(currentScrollLeft / itemOffsetWidth)

            let newIndex

            if (!Number.isNaN(Number(to))) {
                newIndex = horizontalScrollDirection === 'left' ? currentIndex - Number(to) : currentIndex + Number(to)
            }

            if (to === 'end') {
                viewContentContainer &&
                    (horizontalScrollDirection === 'left'
                        ? scroll2EndLeftOrRight(viewContentContainer, { left: 0, behavior: 'smooth' })
                        : scroll2EndLeftOrRight(viewContentContainer, { left: viewItemsWrapper.clientWidth, behavior: 'smooth' }))
            }

            if (newIndex === undefined) {
                return
            }
            scroll2ViewItem({ id, scope }, newIndex, { block: 'nearest', inline: 'nearest' })
        },
        scrollToRecord: (payload: ViewControlScrollToRecordAction<string>['params'] | undefined) => {
            const recordNumber = payload?.scrollToRecord
            if (!recordNumber) {
                return
            }

            const allRecords = store.get(recordPoolAtom)
            const viewRecords = recordPoints.reduce<RecordLikeProtocol[]>((acc, item) => {
                const record = findRecordInPool(allRecords, pointer, item.id)
                if (record) {
                    acc.push(record)
                }
                return acc
            }, [])
            const record = viewRecords.find(item => item.content['ID']?.value === recordNumber)
            const recordId = record?.id

            if (recordId) {
                scrollToRecord({ id, scope }, recordId)
            }
        }
    })

    const handleAppend = useCallback(
        async (params: ViewAppendParams) => {
            const isAppend = await srv.appendDataToDataSource({
                ...params,
                sheetDto: {
                    ...params.sheetDto,
                    envId,
                    triggerWorkflow,
                    repeat,
                    compareFields,
                    importMode
                }
            })
            isAppend && Toast.success(convertTextByLanguage('importSuccess'))
            fetchViewData({
                pageNum: currentPage ?? 1
            })
            return isAppend
        },
        [envId, triggerWorkflow, repeat, compareFields, importMode, convertTextByLanguage, fetchViewData, currentPage]
    )

    return useMemo(() => {
        if (!pointer || !dataSource) {
            const emptyImageUrl = getAssetUrl('empty', 'no_datasource.svg')
            return (
                <CenteredWrapper style={{ width: '100%', height: '100%', overflow: baseBreakpoint.size.overflow }}>
                    <Empty
                        style={{ margin: '32px 0' }}
                        icon={<EmptyImage src={emptyImageUrl} alt="请在「右侧栏>配置」中选择数据来源" />}
                        description="请在「右侧栏>配置」中选择数据来源"
                    />
                </CenteredWrapper>
            )
        }

        // 首次加载未完成时（包括请求未开始和请求进行中），不渲染内容，避免闪现"暂无记录"
        if (firstRef.current && !fetchViewRecordsValue) {
            return null
        }
        // 首次加载完成，标记为非首次
        if (firstRef.current && fetchViewRecordsValue) {
            firstRef.current = false
        }

        return (
            <AnimationDecorators
                blockId={id}
                blockName={title}
                data-block-type={viewType}
                data-stop-action-propagation
                animation={baseBreakpoint.animation}
                {...rest}
                style={{ width: '100%', height: '100%', overflow: baseBreakpoint.size.overflow, ...rest.style }}
            >
                <SCxViewContainer ref={ref}>
                    {breakPoint ? (
                        <ViewBlockTool
                            appId={appId}
                            envId={envId}
                            uniqueId={uniqueId}
                            dataSource={dataSource}
                            dataSourceList={dataSourceList}
                            tabList={tabList}
                            tabValue={tabValue}
                            blockData={blockData}
                            quickFilterData={quickFilterData}
                            viewIndependentDataOmitRecord={viewIndependentDataOmitRecord}
                            sortCache={sortCache}
                            groupCache={initGroup}
                            tablePropsCache={tablePropsCache}
                            onTabChange={handleTabChange}
                            onSearch={handleSearch}
                            onFilter={handleQuickFilter}
                            onChangeSorter={handleSorterChange}
                            onChangeDisplay={handleDisplayChange}
                            onChangeGroup={handleGroupChange}
                            onAppend={handleAppend}
                            onToolbarActionTrigger={handleAction}
                            onChangePageNum={handleChangePageNum}
                            onRenderButtonTitle={handleRenderRichText}
                        >
                            <ViewBlockProvider
                                uniqueId={uniqueId}
                                blockData={blockData}
                                dataSource={dataSource}
                                recordPoints={recordPoints}
                                recordPoolAtom={recordPoolAtom}
                                dataSourceList={dataSourceList}
                            >
                                <KanbanBoardBlock
                                    tablePropsCache={tablePropsCache}
                                    blockWidth={blockWidth}
                                    cachedKanbanSort={kanbanColumnsSortCache}
                                    onCellChange={handleValueChange}
                                    onRecordOperatorActionTrigger={handleRecordOperatorAction}
                                    onRecordClick={handleRecordClickEvent}
                                    onRenderRichText={handleRenderRichText}
                                    onChangeCachedKanbanSort={setKanbanColumnsSortCache}
                                    handleClickAdd={handleClickAdd}
                                />
                            </ViewBlockProvider>
                        </ViewBlockTool>
                    ) : null}
                </SCxViewContainer>
                {children}
            </AnimationDecorators>
        )
    }, [
        pointer,
        dataSource,
        firstRef,
        fetchViewRecordsValue,
        recordPoints,
        id,
        title,
        viewType,
        baseBreakpoint.animation,
        baseBreakpoint.size.overflow,
        rest,
        ref,
        breakPoint,
        appId,
        envId,
        uniqueId,
        dataSourceList,
        tabList,
        tabValue,
        blockData,
        quickFilterData,
        viewIndependentDataOmitRecord,
        sortCache,
        initGroup,
        tablePropsCache,
        handleTabChange,
        handleSearch,
        handleQuickFilter,
        handleSorterChange,
        handleDisplayChange,
        handleGroupChange,
        handleAppend,
        handleAction,
        handleChangePageNum,
        handleRenderRichText,
        blockWidth,
        kanbanColumnsSortCache,
        handleValueChange,
        handleRecordOperatorAction,
        handleRecordClickEvent,
        setKanbanColumnsSortCache,
        handleClickAdd,
        children
    ])
}

export default KanbanController
